"use client";

/**
 * ProfileForm
 * REQ-029: Account Settings - Profile information form
 *
 * Form for editing user's first name and last name.
 * Email is displayed as read-only.
 * Uses react-hook-form with zod validation.
 */

import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// ==========================================================================
// Schema
// ==========================================================================

export const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(50, "First name must be 50 characters or less"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(50, "Last name must be 50 characters or less"),
});

export type ProfileFormData = z.infer<typeof profileSchema>;

// ==========================================================================
// Props
// ==========================================================================

export interface ProfileFormProps {
  /** Current user profile data */
  user: {
    firstName: string | null;
    lastName: string | null;
    email: string;
  };
  /** Callback when form is submitted with valid data */
  onSubmit: (data: ProfileFormData) => Promise<void>;
  /** Test ID for E2E testing */
  "data-testid"?: string;
}

// ==========================================================================
// Component
// ==========================================================================

/**
 * ProfileForm Component
 *
 * Displays first name, last name (editable) and email (read-only).
 * Validates inputs with zod schema before calling onSubmit.
 *
 * @example
 * ```tsx
 * <ProfileForm
 *   user={{ firstName: "John", lastName: "Doe", email: "john@example.com" }}
 *   onSubmit={async (data) => await accountService.updateProfile(data)}
 * />
 * ```
 */
export function ProfileForm({
  user,
  onSubmit,
  "data-testid": testId = "profile-form",
}: ProfileFormProps): React.JSX.Element {
  const formId = React.useId();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user.firstName || "",
      lastName: user.lastName || "",
    },
  });

  // M4 fix: Sync form values when user prop changes (e.g., after re-fetch)
  React.useEffect(() => {
    reset({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
    });
  }, [user.firstName, user.lastName, reset]);

  return (
    <Card data-testid={testId}>
      <CardHeader
        title="Basic Information"
        subheader="Update your personal information"
      />
      <CardContent>
        <Box
          component="form"
          id={formId}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                {...register("firstName")}
                label="First Name"
                fullWidth
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                data-testid={`${testId}-first-name-input`}
                slotProps={{
                  htmlInput: {
                    "data-testid": `${testId}-first-name-field`,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                {...register("lastName")}
                label="Last Name"
                fullWidth
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                data-testid={`${testId}-last-name-input`}
                slotProps={{
                  htmlInput: {
                    "data-testid": `${testId}-last-name-field`,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Email"
                value={user.email}
                fullWidth
                helperText="Contact support to change your email address"
                data-testid={`${testId}-email-input`}
                slotProps={{
                  input: { readOnly: true },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
        <Button
          type="submit"
          form={formId}
          variant="contained"
          disabled={isSubmitting || !isDirty}
          data-testid={`${testId}-submit-btn`}
        >
          {isSubmitting ? "Saving..." : "Save Changes"}
        </Button>
      </CardActions>
    </Card>
  );
}
